import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { formatMoney } from './contact';

type Row = { productId: number; productName: string; listPrice: number; price: number; source: string; specialPrice?: number | null; suggestedRetailPrice?: number | null };

interface Props {
  customer: { id: number; name: string } | null;
  onClose: () => void;
}

/** Precio pactado por producto con un cliente (gana sobre la lista) y su PVP sugerido. */
const CustomerPricesDialog: React.FC<Props> = ({ customer, onClose }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Desde cuándo rige cada pactado (sale del historial de precios).
  const [since, setSince] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!customer) return;
    setLoading(true);
    setError(null);
    api.getCustomerPrices(customer.id)
      .then(r => setRows(r))
      .catch(e => setError(e?.response?.data?.message || 'No se pudieron cargar los precios'))
      .finally(() => setLoading(false));
    api.getPriceHistory({ customerId: customer.id })
      .then(h => {
        const m: Record<number, string> = {};
        for (const r of h) if (!(r.productId in m)) m[r.productId] = r.effectiveFrom; // viene del más nuevo al más viejo
        setSince(m);
      })
      .catch(() => setSince({}));
  }, [customer]);

  const set = (id: number, patch: Partial<Row>) => setRows(prev => prev.map(r => (r.productId === id ? { ...r, ...patch } : r)));

  const save = async () => {
    if (!customer) return;
    setSaving(true);
    try {
      await api.setCustomerPrices(customer.id, rows
        .filter(r => (r.specialPrice || 0) > 0)
        .map(r => ({ productId: r.productId, price: Number(r.specialPrice), suggestedRetailPrice: r.suggestedRetailPrice ? Number(r.suggestedRetailPrice) : null })));
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron guardar los precios');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!customer} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Precios pactados · {customer?.name}</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Dejá vacío para usar el precio de su lista. Al venderle, el sistema completa este precio solo.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell align="right">Su lista hoy</TableCell>
              <TableCell align="right">Precio pactado</TableCell>
              <TableCell align="right">PVP sugerido</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.productId}>
                <TableCell>{r.productName}</TableCell>
                <TableCell align="right">
                  {formatMoney(r.source === 'cliente' ? r.listPrice : r.price)}
                  <Typography variant="caption" color="text.secondary" display="block">{r.source === 'cliente' ? 'lista' : r.source}</Typography>
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" value={r.specialPrice ?? ''} sx={{ width: 120 }}
                    onChange={e => set(r.productId, { specialPrice: e.target.value === '' ? null : Number(e.target.value) })} />
                  {r.specialPrice && since[r.productId] && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      desde {new Date(since[r.productId]).toLocaleDateString('es-AR', { timeZone: 'UTC' })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" value={r.suggestedRetailPrice ?? ''} sx={{ width: 120 }}
                    onChange={e => set(r.productId, { suggestedRetailPrice: e.target.value === '' ? null : Number(e.target.value) })} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerPricesDialog;
